import type { PrizeHistoryData, UserRewardStatusDetailData } from '@/schemas/v2'

export type RewardActionButtonLabelParams = {
  t: (key: string, defaultValue: string) => string
  isDlcExchanged: boolean
  isGiftCodeExchanged: boolean
  isGiftCodeAchievedActive: boolean
  status: UserRewardStatusDetailData['status'] | undefined
  prizes: PrizeHistoryData[]
  selectionType: UserRewardStatusDetailData['prizeSelectionType']
  isRepeatable: boolean
  isAchievedLotteryLose: boolean
  isApplicationFormAchieved: boolean
  isAchievedCouponRepeatable: boolean
  isAchievedCouponNotRepeatable: boolean
  isAchievedArPhotoRepeatable: boolean
  isAchievedArPhoto: boolean
  isAchievedGiftCodeRepeatable: boolean
  isAchievedGiftCodeWithoutMemory: boolean
  isAchievedGifteeRepeatable: boolean
  isAchievedDlcRepeatable: boolean
  isAchievedItemRepeatable: boolean
  isAchievedItem: boolean
}

/**
 * 複数回獲得可能（isRepeatable=true）で、かつ現在が完了系ステータスのとき
 * 「再チャレンジ可能」を示すラベルを返す。
 * LOTTERY の場合は「抽選する」、それ以外は「特典を獲得する」。
 */
function getRepeatableLabel(
  selectionType: UserRewardStatusDetailData['prizeSelectionType'],
  t: (key: string, defaultValue: string) => string,
): { label: string } {
  if (selectionType === 'LOTTERY') return { label: t('reward_detail.lottery', '抽選する') }
  return { label: t('reward_detail.acquire', '特典を獲得する') }
}

/**
 * 特典詳細のメインボタンのラベルを返す
 * */
export function getRewardActionButtonLabel({
  t,
  isDlcExchanged,
  isGiftCodeExchanged,
  isGiftCodeAchievedActive,
  status,
  prizes,
  selectionType,
  isRepeatable,
  isAchievedLotteryLose,
  isApplicationFormAchieved,
  isAchievedCouponRepeatable,
  isAchievedCouponNotRepeatable,
  isAchievedArPhotoRepeatable,
  isAchievedArPhoto,
  isAchievedGiftCodeRepeatable,
  isAchievedGiftCodeWithoutMemory,
  isAchievedGifteeRepeatable,
  isAchievedDlcRepeatable,
  isAchievedItemRepeatable,
  isAchievedItem,
}: RewardActionButtonLabelParams): { label: string } {
  // DLC + EXCHANGED + !isRepeatable → active「特典にアクセスする」
  if (isDlcExchanged) return { label: t('reward_detail.download_prizes', '特典にアクセスする') }

  // GIFT_CODE + EXCHANGED → isRepeatable により文言変更
  if (isGiftCodeExchanged) {
    if (isRepeatable) return getRepeatableLabel(selectionType, t)
    return { label: t('reward_detail.acquire', '特典を獲得する') }
  }

  // GIFT_CODE + ACHIEVED + セッションメモリあり + !isRepeatable → active「特典を獲得する」
  if (isGiftCodeAchievedActive) return { label: t('reward_detail.acquire', '特典を獲得する') }

  switch (status) {
    case 'UNOBTAINED':
      if (prizes.some((p) => p.prize?.fulfillmentType === 'APPLICATION_FORM'))
        return { label: t('reward_detail.application_condition_not_met', '応募条件未達成') }
      return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
    case 'ACHIEVABLE':
      if (selectionType === 'LOTTERY') return { label: t('reward_detail.lottery', '抽選する') }
      if (prizes.some((p) => p.prize?.fulfillmentType === 'APPLICATION_FORM'))
        return { label: t('reward_detail.apply_for_prize', '特典に応募する') }
      if (prizes.some((p) => p.prize?.type === 'COUPON'))
        return { label: t('reward_detail.use_coupon', 'クーポンを使用する') }
      if (prizes.some((p) => p.prize?.type === 'DLC'))
        return { label: t('reward_detail.download_prizes', '特典にアクセスする') }
      if (prizes.some((p) => p.prize?.type === 'AR_PHOTO'))
        return { label: t('reward_detail.launch_ar_camera', 'ARカメラを起動する') }
      return { label: t('reward_detail.acquire', '特典を獲得する') }
    case 'UNAPPLIED':
      // UNAPPLIED + repeatable: 応募条件が未達成であることを示す（次回チャレンジ不可ではなく条件未達成）
      if (isRepeatable) return { label: t('reward_detail.application_condition_not_met', '応募条件未達成') }
      return { label: t('reward_detail.apply_for_prize', '特典に応募する') }
    case 'ACHIEVED': {
      if (isAchievedLotteryLose) {
        // LOTTERY 全ハズレ + repeatable → 次回チャレンジ可能
        if (isRepeatable) return getRepeatableLabel('LOTTERY', t)
        return { label: t('reward_detail.lottery_lost', '抽選済み') }
      }
      if (isApplicationFormAchieved) return { label: t('reward_detail.apply_for_prize', '特典に応募する') }
      if (isAchievedCouponRepeatable) return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
      if (isAchievedCouponNotRepeatable) return { label: t('reward_detail.use_coupon', 'クーポンを使用する') }
      if (isAchievedArPhotoRepeatable) return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
      // AR_PHOTO + !isRepeatable → AR カメラ起動
      if (isAchievedArPhoto) return { label: t('reward_detail.launch_ar_camera', 'ARカメラを起動する') }
      if (isAchievedGiftCodeRepeatable) return getRepeatableLabel(selectionType, t)
      // GIFT_CODE + !isRepeatable + !hasGiftCodeInMemory (リロード後) → disabled「獲得済み」
      if (isAchievedGiftCodeWithoutMemory) return { label: t('reward_detail.acquired', '獲得済み') }
      // GIFT_CODE + !isRepeatable + hasGiftCodeInMemory → active「特典を獲得する」（isGiftCodeAchievedActive の早期 return で処理済み）
      if (isAchievedGifteeRepeatable) return getRepeatableLabel(selectionType, t)
      // GIFTEE + !isRepeatable → active「特典を獲得する」
      if (prizes.some((p) => !p.isLotteryLost && p.prize?.type === 'GIFTEE')) {
        return { label: t('reward_detail.acquire', '特典を獲得する') }
      }
      if (isAchievedDlcRepeatable) return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
      // DLC + !isRepeatable → active「特典にアクセスする」
      if (prizes.some((p) => !p.isLotteryLost && p.prize?.type === 'DLC')) {
        return { label: t('reward_detail.download_prizes', '特典にアクセスする') }
      }
      if (isAchievedItemRepeatable) return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
      // ITEM+NONE+!isRepeatable → active「特典を獲得する」
      if (isAchievedItem) return { label: t('reward_detail.acquire', '特典を獲得する') }
      return { label: t('reward_detail.acquired_reopen', '獲得済み（再表示）') }
    }
    case 'EXCHANGED': {
      // GIFTEE: LOTTERY → active、NORMAL → disabled（ラベルは共通）
      if (prizes.some((p) => p.prize?.type === 'GIFTEE')) {
        if (isRepeatable) return getRepeatableLabel(selectionType, t)
        return { label: t('reward_detail.receive', '受け取る') }
      }
      // DLC: !isRepeatable は isDlcExchanged で先処理済み。repeatable=true のみここに到達
      if (prizes.some((p) => p.prize?.type === 'DLC')) {
        return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
      }
      // COUPON
      if (prizes.some((p) => p.prize?.type === 'COUPON')) {
        if (isRepeatable) return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
        return { label: t('reward_detail.used_coupon', '使用済み') }
      }
      // AR_PHOTO: EXCHANGED + !isRepeatable → ARカメラ起動（ACHIEVED と同じ文言）
      if (prizes.some((p) => p.prize?.type === 'AR_PHOTO')) {
        if (isRepeatable) return getRepeatableLabel(selectionType, t)
        return { label: t('reward_detail.launch_ar_camera', 'ARカメラを起動する') }
      }
      // ITEM + CLERK_REDEMPTION
      if (prizes.some((p) => p.prize?.type === 'ITEM' && p.prize?.fulfillmentType === 'CLERK_REDEMPTION')) {
        if (isRepeatable) return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
        return { label: t('reward_detail.acquired', '獲得済み') }
      }
      // ITEM + APPLICATION_FORM（応募フロー専用ラベルを維持）
      if (prizes.some((p) => p.prize?.type === 'ITEM' && p.prize?.fulfillmentType === 'APPLICATION_FORM')) {
        return { label: t('reward_detail.applied', '応募済み') }
      }
      // ITEM + NONE
      if (prizes.some((p) => p.prize?.type === 'ITEM')) {
        if (isRepeatable) return getRepeatableLabel(selectionType, t)
        return { label: t('reward_detail.exchanged', '交換済み') }
      }
      return { label: t('reward_detail.exchanged', '交換済み') }
    }
    case 'USED':
      return { label: t('reward_detail.used', '使用済み') }
    case 'APPLIED':
      if (isRepeatable) return { label: t('reward_detail.condition_not_met', '獲得条件未達成') }
      return { label: t('reward_detail.applied', '応募済み') }
    case 'LOST':
      return { label: t('reward_detail.lottery_lost', '抽選済み') }
    case 'ENDED':
    case 'SOLD_OUT':
      return { label: t('reward_detail.finished', '終了しました') }
    default:
      return { label: t('reward_detail.acquire', '特典を獲得する') }
  }
}
